import { Logic } from '~common/ui-logic/logic'
import { executeTask } from '~common/ui-logic/tasks'
import type { TaskState } from '~/services/cache/types'
import type { Services } from '~/services/types'

export interface ErrorMessage {
    id: string
    message: string
    timestamp: number
}

export interface ShowErrorEvent {
    type: 'showError'
    message: string
    id?: string
}

export interface ClearErrorEvent {
    type: 'clearError'
    id: string
}

export interface ClearAllErrorsEvent {
    type: 'clearAllErrors'
}

export type ErrorEvent = ShowErrorEvent | ClearErrorEvent | ClearAllErrorsEvent

export interface ErrorDisplayProps {
    services: Services
    autoHideDuration?: number
}

export interface ErrorDisplayDependencies extends ErrorDisplayProps {}

export interface ErrorDisplayState {
    loadState: TaskState
    errors: ErrorMessage[]
}

export class ErrorDisplayLogic extends Logic<
    ErrorDisplayDependencies,
    ErrorDisplayState
> {
    private autoHideTimeouts: Map<string, NodeJS.Timeout> = new Map()

    getInitialState = (): ErrorDisplayState => ({
        loadState: 'pristine',
        errors: [],
    })

    private unsubscribeGlobalEvents: () => void = () => {}

    async initialize() {
        await executeTask(this, 'loadState', async () => {
            // Listen for error events from anywhere in the app
            this.unsubscribeGlobalEvents =
                this.deps.services.globalEvents.listen((event) => {
                    switch (event.type) {
                        case 'showError':
                            this.showError(event.message, event.id)
                            break
                        case 'clearError':
                            this.clearError(event.id)
                            break
                        case 'clearAllErrors':
                            this.clearAllErrors()
                            break
                    }
                })
        })
    }

    showError(message: string, id?: string) {
        const errorId =
            id || `error-${Date.now()}-${Math.random().toString(36).slice(2)}`
        const error: ErrorMessage = {
            id: errorId,
            message,
            timestamp: Date.now(),
        }

        this.setState({
            errors: [...this.state.errors, error],
        })

        // Auto-hide after specified duration (disabled by default, 0 = no auto-hide)
        if (this.deps.autoHideDuration && this.deps.autoHideDuration > 0) {
            const timeoutId = setTimeout(() => {
                this.clearError(errorId)
            }, this.deps.autoHideDuration)

            this.autoHideTimeouts.set(errorId, timeoutId)
        }
    }

    clearError(id: string) {
        // Clear any auto-hide timeout for this error
        const timeoutId = this.autoHideTimeouts.get(id)
        if (timeoutId) {
            clearTimeout(timeoutId)
            this.autoHideTimeouts.delete(id)
        }

        this.setState({
            errors: this.state.errors.filter((error) => error.id !== id),
        })
    }

    clearAllErrors() {
        // Clear all auto-hide timeouts
        for (const timeoutId of this.autoHideTimeouts.values()) {
            clearTimeout(timeoutId)
        }
        this.autoHideTimeouts.clear()

        this.setState({
            errors: [],
        })
    }

    async cleanup() {
        // Clear all timeouts when component unmounts
        for (const timeoutId of this.autoHideTimeouts.values()) {
            clearTimeout(timeoutId)
        }
        this.autoHideTimeouts.clear()
        this.unsubscribeGlobalEvents()
    }
}
